import { getPatientById } from "@/core/patients/patient-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import type {
  PatientConsumableCharge,
  PatientConsumableChargeInput,
  PatientConsumableChargeLine,
  PatientConsumableChargeStatus,
} from "./types";

export type {
  PatientConsumableCharge,
  PatientConsumableChargeInput,
  PatientConsumableChargeLine,
  PatientConsumableChargeStatus,
} from "./types";
export { PATIENT_CONSUMABLE_STATUS_LABELS } from "./types";

const CHARGES_KEY = "hospitol.patientConsumableCharges";
const SEQ_KEY = "hospitol.patientConsumableChargeSeq";
const CHARGES_EVENT = "hospitol:patient-consumables";

function nextChargeNumber(): string {
  const seq = Number(getStorageItem(SEQ_KEY) ?? "0") + 1;
  setStorageItem(SEQ_KEY, String(seq));
  return `CONS-${String(seq).padStart(5, "0")}`;
}

function lineId(): string {
  return `pcl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function normalizeLine(raw: Record<string, unknown>): PatientConsumableChargeLine {
  return {
    id: String(raw.id ?? ""),
    productId: String(raw.productId ?? ""),
    productLabel: String(raw.productLabel ?? ""),
    quantity: Math.max(1, Number(raw.quantity ?? 1)),
    stockState: (raw.stockState as PatientConsumableChargeLine["stockState"]) ?? "none",
  };
}

function normalizeCharge(raw: Record<string, unknown>): PatientConsumableCharge {
  return {
    id: String(raw.id ?? ""),
    chargeNumber: String(raw.chargeNumber ?? ""),
    patientId: String(raw.patientId ?? ""),
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    stayId: raw.stayId ? String(raw.stayId) : null,
    lines: Array.isArray(raw.lines)
      ? (raw.lines as Record<string, unknown>[]).map(normalizeLine)
      : [],
    status: (raw.status as PatientConsumableChargeStatus) ?? "draft",
    billingDocumentId: raw.billingDocumentId ? String(raw.billingDocumentId) : null,
    notes: String(raw.notes ?? ""),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readCharges(): PatientConsumableCharge[] {
  return readJson<Record<string, unknown>[]>(CHARGES_KEY, []).map(normalizeCharge);
}

function writeCharges(charges: PatientConsumableCharge[]): void {
  writeJson(CHARGES_KEY, charges);
  window.dispatchEvent(new CustomEvent(CHARGES_EVENT));
}

export class PatientConsumableValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PatientConsumableValidationError";
  }
}

export function listPatientConsumableCharges(): PatientConsumableCharge[] {
  return readCharges().sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function listPatientConsumableChargesByPatient(patientId: string): PatientConsumableCharge[] {
  return listPatientConsumableCharges().filter((c) => c.patientId === patientId);
}

export function getPatientConsumableChargeById(id: string): PatientConsumableCharge | null {
  return listPatientConsumableCharges().find((c) => c.id === id) ?? null;
}

export function createPatientConsumableCharge(
  input: PatientConsumableChargeInput,
): PatientConsumableCharge {
  if (!input.patientId || !getPatientById(input.patientId)) {
    throw new PatientConsumableValidationError("Patient introuvable.");
  }
  if (!input.lines.length) {
    throw new PatientConsumableValidationError("Ajoutez au moins un consommable.");
  }

  const now = new Date().toISOString();
  const charge: PatientConsumableCharge = {
    id: `pcc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    chargeNumber: nextChargeNumber(),
    patientId: input.patientId,
    admissionId: input.admissionId ?? null,
    stayId: input.stayId ?? null,
    lines: input.lines.map((line) => ({
      id: lineId(),
      productId: line.productId,
      productLabel: line.productLabel,
      quantity: Math.max(1, line.quantity),
      stockState: "none",
    })),
    status: "draft",
    billingDocumentId: null,
    notes: input.notes?.trim() ?? "",
    createdAt: now,
    updatedAt: now,
  };

  writeCharges([charge, ...readCharges()]);
  return charge;
}

export function patchPatientConsumableCharge(
  id: string,
  patch: Partial<Pick<PatientConsumableCharge, "status" | "billingDocumentId" | "lines" | "notes">>,
): PatientConsumableCharge | null {
  const list = readCharges();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;
  const updated: PatientConsumableCharge = {
    ...list[index]!,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  list[index] = updated;
  writeCharges(list);
  return updated;
}

export function patchPatientConsumableChargeLine(
  chargeId: string,
  lineIdValue: string,
  patch: Partial<Pick<PatientConsumableChargeLine, "stockState">>,
): PatientConsumableCharge | null {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge) return null;
  const lines = charge.lines.map((line) =>
    line.id === lineIdValue ? { ...line, ...patch } : line,
  );
  return patchPatientConsumableCharge(chargeId, { lines });
}

export function cancelPatientConsumableCharge(id: string): PatientConsumableCharge | null {
  const charge = getPatientConsumableChargeById(id);
  if (!charge || charge.status !== "draft") return null;
  return patchPatientConsumableCharge(id, { status: "cancelled" });
}
